import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message } from 'antd'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { AssetCorrosionAnalysisData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useMemo } from 'react'

const requiredAssetCorrosionAnalysisFields = ['analysis']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.assetCorrosionAnalysisData,
    [formData.assetCorrosionAnalysisData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page
      size='A4'
      style={PDFStyles.page}
      id='asset-corrosion-analysis'
      wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'asset-corrosion-analysis', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Asset Corrosion Analysis' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.analysis ? (
            <Text style={{ fontSize: 10, lineHeight: 1.5 }}>
              {data.analysis}
            </Text>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<AssetCorrosionAnalysisData>()
  const { formData, handleAssetCorrosionAnalysisFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: AssetCorrosionAnalysisData) => {
      try {
        await handleAssetCorrosionAnalysisFinish(values.analysis || '')
        message.success('Asset corrosion analysis saved successfully!')
      } catch {
        message.error('Failed to save asset corrosion analysis')
      }
    },
    [handleAssetCorrosionAnalysisFinish]
  )

  const initialValues = React.useMemo(
    () => formData.assetCorrosionAnalysisData,
    [formData.assetCorrosionAnalysisData]
  )

  return (
    <StyledForm<AssetCorrosionAnalysisData>
      form={form}
      name='assetCorrosionAnalysisForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='analysis'
        label='Analysis'
        rules={[
          { required: true, message: 'Please enter the corrosion analysis' },
        ]}>
        <Input.TextArea
          rows={6}
          placeholder='Enter detailed corrosion analysis...'
        />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Asset Corrosion Analysis
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const AssetCorrosionAnalysisSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'assetCorrosionAnalysis',
  description: 'Detailed corrosion analysis',
  title: 'Asset Corrosion Analysis',
  requiredFields: requiredAssetCorrosionAnalysisFields,
}

export const AssetCorrosionAnalysisSectionPDF = RenderPDF
